/* @flow */

import fetch from 'isomorphic-fetch';

import { loginRoute } from 'src/shared/app/base/route/setting/routeSettings';
import { getRoutePath } from 'src/shared/app/base/route/util/routeUtils';
import {
  getEnvironment,
  getAPIURL,
  isErrorReportingEnabled,
  getVersion,
} from 'src/shared/app/base/selector/appEnvSelectors';
import {
  removeTokenFromCookie,
  unregisterSentryUserFromBrowser,
} from 'src/shared/app/authentication/util/authenticationUtils';
import { serializeUrl } from 'src/shared/core/util/urlUtil';
import { getAuthenticationToken } from 'src/shared/app/authentication/selector/base/appAuthenticationBaseSelectors';

// HTTP methods
export function get(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Response> {
  return _fetch(getState, url, data);
}

export function post(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Response> {
  return _fetch(getState, url, data, 'POST');
}

export function patch(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Response> {
  return _fetch(getState, url, data, 'PATCH');
}

export function put(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Response> {
  return _fetch(getState, url, data, 'PUT');
}

export function deleteFn(
  getState: () => Object,
  url: any,
  data?: Object = {},
): Promise<Response> {
  return _fetch(getState, url, data, 'DELETE');
}

export function smartGet(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Object> {
  return _fetch(getState, url, data, 'GET').then(parseResponse);
}

export function smartPost(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Object> {
  return _fetch(getState, url, data, 'POST').then(parseResponse);
}

export function smartDelete(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Object> {
  return _fetch(getState, url, data, 'DELETE').then(parseResponse);
}

// Parse

export function parseResponse(response: Response): any {
  const { url, status, statusText } = response;
  // If status code is 204, don't parse it to JSON or it causes an error. Return
  // an empty object instead of the response, otherwise actions may try to
  // save the response directly in the redux state, which will throw an
  // exception
  if (status === 204) {
    return {};
  } else if (status >= 200 && status < 300) {
    return response.json();
  }

  const errorName = 'APIError';

  return response
    .json()
    .catch((e) => {
      const error = new Error(
        `Failed to parse JSON response from ${url}
          Status: ${status} ${statusText}
          Original error: ${e.toString()}`,
      );

      error.name = errorName;
      // $FlowIssue
      error.response = response;

      throw error;
    })
    .then((json) => {
      const error = new Error(
        `Call to ${url} failed
          Status: ${status} ${statusText}`,
      );

      error.name = errorName;
      // $FlowIssue
      error.response = response;
      // $FlowIssue
      error.json = json;

      throw error;
    });
}

/*
|------------------------------------------------------------------------------
| PRIVATE FUNCTIONS
|------------------------------------------------------------------------------
*/

const _fetch = (
  getState: () => Object,
  url: any,
  data?: Object,
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE' = 'GET',
): Promise<Response> => {
  const params = {
    method,
    headers: {},
  };

  const state = getState();
  const token = getAuthenticationToken(state);
  const { language } = state.app;

  let _url = typeof url === 'function' ? url(getAPIURL(state)) : url;

  if (token) {
    params.headers.Authorization = `Token ${token}`;
  }

  if (language) {
    params.headers['Accept-Language'] = language;
  }

  // Update user agent for calls made to the API by the node server
  if (typeof window === 'undefined') {
    // $FlowIssue
    params.headers['User-Agent'] = `${getVersion(state)} ${getEnvironment(
      state,
    )}`;
  }

  if (data && typeof data === 'object') {
    if (method === 'GET') {
      _url = serializeUrl(_url, data);
    } else if (typeof window !== 'undefined' && data instanceof FormData) {
      // $FlowIssue
      params.body = data;
    } else {
      params.headers['Content-Type'] = 'application/json';

      // $FlowIssue
      params.body = JSON.stringify(data);
    }
  }

  return fetch(_url, params).then((response: Response) => {
    // If invalid token (401 UNAUTHORIZED)
    if (response.status === 401) {
      if (typeof window !== 'undefined') {
        removeTokenFromCookie();

        if (isErrorReportingEnabled(state)) {
          unregisterSentryUserFromBrowser();
        }

        window.location = getRoutePath(loginRoute);
      } else {
        const error = new Error('Invalid token');

        // $FlowIssue
        error.response = response;

        throw error;
      }
    }

    return response;
  });
};
